export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SELECTED_BOT_ID_KEY = 'selected_bot_id';
const SELECTED_BOT_NAME_KEY = 'selected_bot_name';
const LEGACY_PROFILE_KEY = 'selectedProfile';

export interface AccessBotOption {
  id: string;
  name: string;
  required_role: string;
  description: string;
  query_endpoint?: string;
  conversations_endpoint?: string;
}

export interface AccessOptionsResponse {
  username: string;
  roles: string[];
  is_admin?: boolean;
  available_bots: AccessBotOption[];
  default_bot_id?: string | null;
}

// Per-bot backend endpoints. Mirrors BOT_CATALOG in the backend (bot_access.py):
// each bot is served by its own role-gated routes, so the frontend must route
// requests to the right endpoint instead of always using /query & /conversations.
interface BotEndpoints {
  query: string;
  conversations: string;
}

const BOT_ENDPOINTS: Record<string, BotEndpoints> = {
  'finance-report': { query: '/query', conversations: '/conversations' },
  'direct-taxation': { query: '/query-tax', conversations: '/conversations-tax' },
  'indirect-taxation': { query: '/query-indirect', conversations: '/conversations-indirect' },
};

const DEFAULT_BOT_ENDPOINTS: BotEndpoints = BOT_ENDPOINTS['finance-report'];

function resolveBotEndpoints(botId?: string | null): BotEndpoints {
  return (botId && BOT_ENDPOINTS[botId]) || DEFAULT_BOT_ENDPOINTS;
}

export interface QueryPayload {
  query: string;
  top_k?: number;
  conversation_id?: string | null;
  enable_web_search?: boolean;
  bot_id: string;
}

export interface QueryResponse {
  answer: string;
  sources?: any[];
  chart?: any;
  table?: any;
  conversation_id?: string | null;
}

export interface ConversationsResponse {
  conversations: any[];
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function buildHeaders(token: string) {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function getErrorMessage(data: any, fallback: string) {
  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  if (typeof data?.detail === 'string' && data.detail.trim()) {
    return data.detail;
  }

  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }

  return fallback;
}

async function parseJsonResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(
      getErrorMessage(data, fallbackMessage),
      response.status,
      data
    );
  }

  return data as T;
}

export async function getAccessOptions(token: string) {
  const response = await fetch(`${API_BASE_URL}/auth/access-options`, {
    headers: buildHeaders(token),
  });

  return parseJsonResponse<AccessOptionsResponse>(
    response,
    'Failed to load access options'
  );
}

export async function selectBot(token: string, botId: string) {
  const response = await fetch(`${API_BASE_URL}/auth/select-bot`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ bot_id: botId }),
  });

  return parseJsonResponse(response, 'Bot selection failed');
}

export async function queryBot(token: string, payload: QueryPayload) {
  const { query: queryEndpoint } = resolveBotEndpoints(payload.bot_id);
  const response = await fetch(`${API_BASE_URL}${queryEndpoint}`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(payload),
  });

  return parseJsonResponse<QueryResponse>(response, 'Query failed');
}

export async function getConversations(token: string, botId: string) {
  const { conversations } = resolveBotEndpoints(botId);
  const response = await fetch(`${API_BASE_URL}${conversations}`, {
    headers: buildHeaders(token),
  });

  return parseJsonResponse<ConversationsResponse>(
    response,
    'Failed to load conversations'
  );
}

export async function getConversation(token: string, botId: string, conversationId: string) {
  const { conversations } = resolveBotEndpoints(botId);
  const response = await fetch(`${API_BASE_URL}${conversations}/${conversationId}`, {
    headers: buildHeaders(token),
  });

  return parseJsonResponse<any>(response, 'Failed to load conversation');
}

export async function deleteConversation(token: string, botId: string, conversationId: string) {
  const { conversations } = resolveBotEndpoints(botId);
  const response = await fetch(`${API_BASE_URL}${conversations}/${conversationId}`, {
    method: 'DELETE',
    headers: buildHeaders(token),
  });

  return parseJsonResponse(response, 'Failed to delete conversation');
}

export function setSelectedBot(botId: string, botName: string) {
  localStorage.setItem(SELECTED_BOT_ID_KEY, botId);
  localStorage.setItem(SELECTED_BOT_NAME_KEY, botName);
  localStorage.removeItem(LEGACY_PROFILE_KEY);
}

export function getSelectedBotId() {
  return localStorage.getItem(SELECTED_BOT_ID_KEY);
}

export function getSelectedBotName() {
  return localStorage.getItem(SELECTED_BOT_NAME_KEY);
}

export function clearSelectedBot() {
  localStorage.removeItem(SELECTED_BOT_ID_KEY);
  localStorage.removeItem(SELECTED_BOT_NAME_KEY);
  localStorage.removeItem(LEGACY_PROFILE_KEY);
}
